// Report rendering for the Reports page. Statement tables use the gvf-*
// classes styled under .report-canvas. Amount cells show the statement's
// own decimal strings so a CSV export can repeat them without csvField
// (a leading minus must stay a minus). Drill targets are buttons; cash
// flow, by location, and any balance-sheet line that does not match a
// ledger closing stay plain text.

import { amVisibleDisplayText } from "../../../ingest-worker/src/am_visible_text.js";
import {
  barChartBlock,
  SERIES_COLORS,
  type Chart,
} from "../../../financials-component/src/charts.js";
import {
  absCents,
  centsToDecimalString,
  parseDecimal,
  toChartNumber,
} from "../../../financials-component/src/decimal.js";
import { clear, h } from "../../../financials-component/src/dom.js";
import { csvField } from "../csv_field.js";
import {
  NO_COMPANY_NAMES,
  accountLabel,
  accountLeafLabel,
  lineLabelText,
  monthText,
  type AccountLabelContext,
} from "../ui/display-words.js";
import { formatCents } from "../ui/money.js";
import type { AccountPath, CompanySlug } from "../types.js";
import type { PnlLine, PnlSection } from "../../../financials-component/src/types.js";
import type { ByLocationRow } from "../../../financials-component/src/types.js";
import type {
  BalanceSheetData,
  ByLocationData,
  CashFlowData,
  CashFlowSection,
  PnlData,
  PyComparisonData,
  ReportDataMap,
  ReportId,
  ReportSnapshot,
} from "./types.js";

export type { Chart } from "../../../financials-component/src/charts.js";

const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
] as const;

export interface StatementRenderOptions {
  /**
   * The one account-label context (rd13 L1): ledger company segment -> the
   * company's plain name, so a report line for money between companies reads
   * "Due from <company>" and never the internal key.
   */
  companies: AccountLabelContext;
  /**
   * Open month. The balance sheet still names the day it is for (rd13 L1):
   * the date comes from `asOfText` or the payload, never from the month end.
   */
  openMonth: boolean;
  /**
   * The balance sheet's "as of" date, already in plain words — the page's own
   * coverage line, so the table and the strip cannot name two dates. Omitted:
   * the payload's `asOf` is used.
   */
  asOfText?: string;
  onAccount?: (account: string) => void;
  onPrefix?: (prefix: string) => void;
  /**
   * rd15 A11: open the journal for the accounts a cash-flow line bucketed. A
   * line with no accounts is not a control: there would be nothing to filter.
   */
  onAccounts?: (accounts: readonly string[]) => void;
  /**
   * rd15 A12: open the journal for a by-location row's own name. The name is
   * the report's current spelling, and the ledger folds retired spellings to
   * it, so the list holds the row's entries.
   */
  onLocation?: (location: string) => void;
  /** Account → closing. A balance-sheet line drills only when both match. */
  closings?: ReadonlyMap<string, string>;
  /** The one comparison column, when the person asked for one. */
  compare?: ReportCompare;
}

/**
 * One comparison column (rd15 R1): the same report over another period, so the
 * table can add the comparison figure and its difference beside each amount.
 *
 * `label` is the column head in compact period words ("May 2026",
 * "Jan to Mar 2025"); `note` is the one sentence above the tables that spells
 * the period out. Both come from the page, which is the only place that knows
 * which months were read.
 *
 * `difference` is false when the comparison is short: a figure summed from
 * some of the period's months cannot be subtracted from a whole one, so the
 * Difference column is not drawn rather than being computed from what happened
 * to load. Defaults to true.
 */
export interface ReportCompare {
  label: string;
  note: string;
  data: ReportDataMap[ReportId];
  difference?: boolean;
}

export interface ShownTable {
  headers: string[];
  rows: string[][];
  amountColumns: number[];
}

/** Months in this quarter or year, from the first published month through the clock month. Later months are omitted. */
export function statementPeriods(options: {
  preset: "quarter" | "year";
  now: Date;
  firstPublished: string | null;
}): string[] {
  const current = clockPeriod(options.now);
  const year = Number(current.slice(0, 4));
  const month = Number(current.slice(5, 7));
  let start = options.preset === "quarter" ? Math.floor((month - 1) / 3) * 3 + 1 : 1;
  const first = options.firstPublished;
  if (first !== null && /^\d{4}-\d{2}$/.test(first)) {
    const firstYear = Number(first.slice(0, 4));
    const firstMonth = Number(first.slice(5, 7));
    if (firstYear > year) return [];
    if (firstYear === year && firstMonth > month) return [];
    if (firstYear === year && firstMonth > start) start = firstMonth;
  }
  const periods: string[] = [];
  for (let cursor = start; cursor <= month; cursor += 1) {
    periods.push(`${String(year)}-${String(cursor).padStart(2, "0")}`);
  }
  return periods;
}

export function clockPeriod(now: Date): string {
  return `${String(now.getFullYear())}-${String(now.getMonth() + 1).padStart(2, "0")}`;
}

/**
 * "October 2026 is not available yet" — the month and its year, because a
 * range can reach back years and "October" alone names none of them. Never a
 * future month: a range that would end in the future is refused before it is
 * read (see `customRangeMonths`).
 */
export function missingMonthSentence(period: string): string {
  return `${monthWithYear(period)} is not available yet`;
}

/**
 * The same sentence for a month the comparison column needed, so the list makes
 * clear which period was short: "The comparison month October 2025 is not
 * available yet."
 */
export function missingComparisonMonthSentence(period: string): string {
  return `The comparison month ${monthWithYear(period)} is not available yet`;
}

/** "October 2026" from `2026-10`; the token itself when it is not a month. */
function monthWithYear(period: string): string {
  const month = Number(period.slice(5, 7));
  const name = MONTH_NAMES[month - 1];
  if (name === undefined || !/^\d{4}-(0[1-9]|1[0-2])$/.test(period)) return period;
  return `${name} ${period.slice(0, 4)}`;
}

export function composePnl(parts: readonly PnlData[]): PnlData {
  const currency = parts[0]?.currency ?? "USD";
  let periodStart = parts[0]?.periodStart ?? "";
  let periodEnd = parts[0]?.periodEnd ?? "";
  const sectionOrder: string[] = [];
  const sections = new Map<string, PnlSection>();
  let net = 0n;
  let prior: bigint | null = 0n;

  for (const part of parts) {
    if (part.periodStart !== "" && (periodStart === "" || part.periodStart < periodStart)) {
      periodStart = part.periodStart;
    }
    if (part.periodEnd !== "" && part.periodEnd > periodEnd) periodEnd = part.periodEnd;
    net += decimalCents(part.netIncome);
    if (prior !== null) {
      if (part.priorYearNetIncome === null) prior = null;
      else prior += decimalCents(part.priorYearNetIncome);
    }
    for (const section of part.sections) {
      let bucket = sections.get(section.key);
      if (bucket === undefined) {
        bucket = { key: section.key, label: section.label, lines: [], total: "0.00" };
        sections.set(section.key, bucket);
        sectionOrder.push(section.key);
      }
      bucket.total = centsToDecimalString(decimalCents(bucket.total) + decimalCents(section.total));
      for (const line of section.lines) {
        const existing = bucket.lines.find((row) => row.account === line.account);
        if (existing === undefined) {
          bucket.lines.push({
            account: line.account,
            label: line.label,
            amount: line.amount,
            priorYearAmount: line.priorYearAmount,
          });
        } else {
          existing.amount = centsToDecimalString(
            decimalCents(existing.amount) + decimalCents(line.amount),
          );
        }
      }
    }
  }

  return {
    currency,
    periodStart,
    periodEnd,
    sections: sectionOrder.map((key) => sections.get(key)).filter((row): row is PnlSection => row !== undefined),
    netIncome: centsToDecimalString(net),
    priorYearNetIncome: prior === null ? null : centsToDecimalString(prior),
  };
}

/**
 * One cash-flow statement from the months it covers (rd15 R1).
 *
 * The parts arrive oldest first. Opening cash is the first month's opening,
 * which is the day before the range starts, so the range opens where the books
 * opened. Section and line totals are summed, so a range total is the sum of
 * its months.
 *
 * Closing cash is the LAST LOADED month's own closing cash — never opening
 * plus the summed change. Those two are the same figure only when every month
 * in between is loaded: with a month missing the books still closed the last
 * month at its own figure, and deriving the closing from the months that
 * happened to load would print a number the bank never held. When the two
 * disagree the page says so plainly, because the three figures on the
 * statement will not foot to the eye and the reader deserves the reason.
 */
export function composeCashFlow(parts: readonly CashFlowData[]): CashFlowData {
  const currency = parts[0]?.currency ?? "USD";
  let periodStart = parts[0]?.periodStart ?? "";
  let periodEnd = parts[0]?.periodEnd ?? "";
  const sectionOrder: string[] = [];
  const sections = new Map<string, CashFlowSection>();
  let net = 0n;

  for (const part of parts) {
    if (part.periodStart !== "" && (periodStart === "" || part.periodStart < periodStart)) {
      periodStart = part.periodStart;
    }
    if (part.periodEnd !== "" && part.periodEnd > periodEnd) periodEnd = part.periodEnd;
    net += decimalCents(part.netChange);
    for (const section of part.sections) {
      let bucket = sections.get(section.key);
      if (bucket === undefined) {
        bucket = { key: section.key, label: section.label, lines: [], total: "0.00" };
        sections.set(section.key, bucket);
        sectionOrder.push(section.key);
      }
      bucket.total = centsToDecimalString(decimalCents(bucket.total) + decimalCents(section.total));
      for (const line of section.lines) {
        const existing = bucket.lines.find((row) => row.label === line.label);
        if (existing === undefined) {
          bucket.lines.push({ label: line.label, amount: line.amount });
        } else {
          existing.amount = centsToDecimalString(
            decimalCents(existing.amount) + decimalCents(line.amount),
          );
        }
      }
    }
  }

  const openingCash = parts[0]?.openingCash ?? "0.00";
  const netChange = centsToDecimalString(net);
  const last = parts[parts.length - 1];
  const closingCash =
    last?.closingCash !== undefined && last.closingCash !== ""
      ? last.closingCash
      : centsToDecimalString(decimalCents(openingCash) + net);
  return {
    currency,
    periodStart,
    periodEnd,
    openingCash,
    closingCash,
    sections: sectionOrder
      .map((key) => sections.get(key))
      .filter((row): row is CashFlowSection => row !== undefined),
    netChange,
  };
}

/**
 * Closing cash minus (opening cash plus the summed change), in cents. Zero on
 * a whole run of months; non-zero when a month in between is not loaded, which
 * is the one case where the statement's own identity cannot hold and the page
 * has to say why.
 */
export function cashFlowGapCents(data: CashFlowData): bigint {
  return (
    decimalCents(data.closingCash) -
    decimalCents(data.openingCash) -
    decimalCents(data.netChange)
  );
}

/**
 * One by-location statement from the months it covers (rd15 R1). Income,
 * expenses and net are summed per location, leaf rows and parent roll-ups
 * alike, so a range row is the sum of that location's months. A location keeps
 * the place the first month that had it gave it.
 */
export function composeByLocation(parts: readonly ByLocationData[]): ByLocationData {
  const currency = parts[0]?.currency ?? "USD";
  const sumRows = (
    pick: (part: ByLocationData) => readonly ByLocationRow[],
  ): ByLocationRow[] => {
    // A Map keeps insertion order, so the first month that had a location
    // fixes its place and no second list can drift from this one.
    const rows = new Map<string, ByLocationRow>();
    for (const part of parts) {
      for (const row of pick(part)) {
        let bucket = rows.get(row.location);
        if (bucket === undefined) {
          bucket = { location: row.location, income: "0.00", expenses: "0.00", net: "0.00" };
          rows.set(row.location, bucket);
        }
        bucket.income = centsToDecimalString(
          decimalCents(bucket.income) + decimalCents(row.income),
        );
        bucket.expenses = centsToDecimalString(
          decimalCents(bucket.expenses) + decimalCents(row.expenses),
        );
        bucket.net = centsToDecimalString(decimalCents(bucket.net) + decimalCents(row.net));
      }
    }
    return [...rows.values()];
  };
  const parents = sumRows((part) => part.parents ?? []);
  return {
    currency,
    locations: sumRows((part) => part.locations),
    ...(parents.length > 0 ? { parents } : {}),
  };
}

/** Longest shared account-segment prefix for a P&L group total. */
export function groupAccountPrefix(accounts: readonly string[]): string | null {
  const parts = accounts
    .map((account) => account.split(":").filter((segment) => segment !== ""))
    .filter((segments) => segments.length > 0);
  const first = parts[0];
  if (first === undefined) return null;
  let shared = first.length;
  for (const segments of parts) {
    let index = 0;
    while (
      index < shared &&
      index < segments.length &&
      segments[index] === first[index]
    ) {
      index += 1;
    }
    shared = index;
  }
  if (shared === 0) return null;
  const identical = parts.every(
    (segments) =>
      segments.length === first.length &&
      segments.every((segment, index) => segment === first[index]),
  );
  if (identical && shared > 1) shared -= 1;
  return first.slice(0, shared).join(":");
}

export function shownTable<K extends ReportId>(
  report: K,
  data: ReportDataMap[K],
  companies: AccountLabelContext,
  compare?: ReportCompare,
): ShownTable | null {
  const column = compare === undefined ? null : compareColumn(report, compare);
  switch (report) {
    case "pnl":
      return pnlTable(data as PnlData, companies, column);
    case "balance-sheet":
      return balanceTable(data as BalanceSheetData, companies, column);
    case "cash-flow":
      return cashTable(data as CashFlowData, column);
    case "by-location":
      return locationTable(data as ByLocationData);
    case "py-comparison":
      return pyTable(data as PyComparisonData);
    default: {
      const exhaustive: never = report;
      return exhaustive;
    }
  }
}

/** Text columns through csvField. Amount columns stay plain decimals. */
export function tableCsv(table: ShownTable): string {
  const amountColumns = new Set(table.amountColumns);
  const encode = (value: string, index: number): string =>
    amountColumns.has(index) ? value : csvField(value);
  const lines = [table.headers, ...table.rows].map((row) =>
    row.map((value, index) => encode(value, index)).join(","),
  );
  return `${lines.join("\n")}\n`;
}

export function provisionalNoticeOf(data: unknown): string | null {
  if (typeof data !== "object" || data === null || !("provisionalNotice" in data)) return null;
  const notice = data.provisionalNotice;
  if (typeof notice !== "string" || notice.trim() === "") return null;
  // Worker-generated text, shown to a person: strip the line hazards (rule 20).
  return visible(notice);
}

export function renderReport<K extends ReportId>(
  container: HTMLElement,
  report: K,
  snapshot: ReportSnapshot<ReportDataMap[K]>,
  options: StatementRenderOptions,
): Chart[] {
  switch (report) {
    case "pnl":
      return renderPnlTable(container, snapshot as ReportSnapshot<PnlData>, options);
    case "balance-sheet":
      return renderBalanceTable(
        container,
        snapshot as ReportSnapshot<BalanceSheetData>,
        options,
      );
    case "cash-flow":
      return renderCashTable(container, snapshot as ReportSnapshot<CashFlowData>, options);
    case "by-location":
      return renderLocationTable(
        container,
        snapshot as ReportSnapshot<ByLocationData>,
        options,
      );
    case "py-comparison":
      return renderPyTable(container, snapshot as ReportSnapshot<PyComparisonData>);
    default: {
      const exhaustive: never = report;
      throw new Error(`Unknown report id: ${String(exhaustive)}`);
    }
  }
}

export function reportIsEmpty<K extends ReportId>(report: K, data: ReportDataMap[K]): boolean {
  switch (report) {
    case "pnl":
      return (data as PnlData).sections.length === 0;
    case "balance-sheet":
      return (data as BalanceSheetData).sections.length === 0;
    case "cash-flow":
      return (data as CashFlowData).sections.length === 0;
    case "by-location":
      return (data as ByLocationData).locations.length === 0 &&
        ((data as ByLocationData).parents ?? []).length === 0;
    case "py-comparison":
      return false;
    default: {
      const exhaustive: never = report;
      throw new Error(`Unknown report id: ${String(exhaustive)}`);
    }
  }
}

/**
 * The one wording for "this report has no lines to compare" (rd round 12,
 * duplicate-sentence): the canvas and the paginated table read the same copy
 * from here, so the two never drift.
 */
export const NO_COMPARISON_LINES_TEXT = "No comparison lines for this period.";

export function destroyCharts(charts: readonly Chart[]): void {
  for (const chart of charts) {
    try {
      chart.destroy();
    } catch {
      /* already gone */
    }
  }
}

function decimalCents(value: string): bigint {
  return parseDecimal(value);
}

function tryCents(value: string): bigint | null {
  try {
    return parseDecimal(value);
  } catch {
    return null;
  }
}

function visible(value: string): string {
  return amVisibleDisplayText(value);
}

function sameMoney(left: string, right: string): boolean {
  try {
    return parseDecimal(left) === parseDecimal(right);
  } catch {
    return left === right;
  }
}

/**
 * Display cents for one statement line. The contract's P&L is income positive
 * and expenses negative; a person reads both as positive. Balance-sheet
 * liabilities and equity arrive negative (credit balances) and are shown
 * positive. Assets keep their ledger sign so an overdrawn asset stays honest.
 */
export function displayCents(amount: string, report: ReportId, sectionKey: string): bigint {
  const cents = tryCents(amount) ?? 0n;
  // The contract fixes the P&L sign: income positive, expenses negative.
  // Flip expenses rather than taking the absolute value, so a contra line (an
  // expense refund, an income reversal) stays honest and totals stay arithmetic.
  if (report === "pnl" && sectionKey === "expenses") return -cents;
  if (report === "balance-sheet" && (sectionKey === "liabilities" || sectionKey === "equity")) {
    return -cents;
  }
  return cents;
}

function amountSpan(amount: string, displayed?: bigint): HTMLSpanElement {
  const cents = displayed ?? tryCents(amount);
  const negative = cents !== null ? cents < 0n : amount.startsWith("-");
  return h(
    "span",
    {
      class: negative ? "gvf-num gvf-neg" : "gvf-num",
      "data-role": "report-amount",
      "data-amount": amount,
    },
    [cents === null ? visible(amount) : formatCents(cents)],
  );
}

function amountCell(amount: string, displayed?: bigint): HTMLTableCellElement {
  return h("td", { class: "gvf-num gvf-num-cell" }, [amountSpan(amount, displayed)]);
}

/**
 * The one sentence above the tables that names the period being compared with.
 * One home, so the screen and the CSV's own header cannot tell two stories.
 */
function compareNote(compare: CompareColumn | undefined): HTMLElement | null {
  if (compare === undefined) return null;
  return h("p", { class: "gvf-meta", "data-role": "report-compare-note" }, [
    visible(compare.note),
  ]);
}

/** The comparison figure and its difference, in that order. */
function compareCells(
  compare: CompareColumn | undefined,
  key: string,
  sectionKey: string,
  currentDisplayed: bigint,
): HTMLTableCellElement[] {
  if (compare === undefined) return [];
  const other = compare.index.get(key) ?? 0n;
  const otherAmount = centsToDecimalString(other);
  // The contract's own sign goes in data-amount (the CSV repeats it); the eye
  // reads the same flipped sign the Amount column beside it shows.
  const otherDisplayed = displayCents(otherAmount, compare.report, sectionKey);
  const cells = [amountCell(otherAmount, otherDisplayed)];
  if (compare.difference) {
    // Computed from the two compositions already in hand. It is never a second
    // read, so it cannot disagree with the columns beside it.
    cells.push(amountCell(centsToDecimalString(currentDisplayed - otherDisplayed)));
  }
  return cells;
}

/** The extra column heads, when there is a comparison. */
function compareHeadCells(compare: CompareColumn | undefined): HTMLTableCellElement[] {
  if (compare === undefined) return [];
  const heads = [
    h("th", { class: "gvf-num", "data-role": "report-compare-head" }, [visible(compare.label)]),
  ];
  if (compare.difference) heads.push(h("th", { class: "gvf-num" }, ["Difference"]));
  return heads;
}

const TYPE_HEADS = new Set(["assets", "liabilities", "income", "expenses", "equity"]);

function humaniseAccountSegment(segment: string): string {
  return segment
    .replace(/[_-]+/g, " ")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .trim();
}

function looksLikeCompanyCode(segment: string): boolean {
  return /^[A-Z0-9-]{2,}$/.test(segment);
}

function accountParts(account: string): { head: string; body: string[] } {
  const parts = account
    .split(":")
    .map((part) => part.trim())
    .filter((part) => part.length > 0);
  const head = parts[0] !== undefined && TYPE_HEADS.has(parts[0].toLowerCase()) ? parts[0] : "";
  const body = parts.filter(
    (part, index) =>
      !(index === 0 && head !== "") &&
      !(index > 0 && looksLikeCompanyCode(part) && part !== head),
  );
  return { head, body };
}

/** The short readable label: the last account segment, in the shared words. */
export function accountShortLabel(
  account: string,
  companies: AccountLabelContext,
): string {
  if (account.trim() === "") return "";
  return accountLeafLabel(account, companies);
}

function accountParentLabel(
  account: string,
  companies: AccountLabelContext,
): string {
  const { head, body } = accountParts(account);
  const short = accountShortLabel(account, companies);
  if (body.length > 1) {
    const parent = humaniseAccountSegment(body[body.length - 2] ?? "");
    if (parent !== "" && parent.toLowerCase() !== short.toLowerCase()) return parent;
  }
  if (head !== "") return humaniseAccountSegment(head);
  return body.length > 0 ? humaniseAccountSegment(body[0] ?? "") : "";
}

/**
 * The internal slug as the plain key a wire-shaped helper takes (a file name, a
 * cache key, an origin lookup). A Map lookup is the sanctioned slug-to-key
 * boundary: the value is the key itself, never words a person reads (the same
 * helper B4a's Expense audit page carries).
 */
export function slugText(slug: CompanySlug): string {
  return new Map<CompanySlug, string>([[slug, slug]]).get(slug) ?? "";
}

/** The same key boundary for an account path (a file name, a lookup key). */
export function accountText(account: AccountPath): string {
  return new Map<AccountPath, string>([[account, account]]).get(account) ?? "";
}

/**
 * Readable labels for a set of accounts. A label that would appear twice in
 * the same view gains its parent ("Income › Rent" / "Expenses › Rent"); the
 * full account stays in data-account and in the CSV. Never renders a raw
 * account path on the face.
 */
export function disambiguatedAccountLabels(
  accounts: readonly AccountPath[],
  companies: AccountLabelContext,
): Map<string, string> {
  return disambiguatedLabels(
    // The path stays the internal key of the set; only the label is words.
    accounts.map((account) => ({
      account: accountText(account),
      label: accountShortLabel(accountText(account), companies),
    })),
    companies,
  );
}

/**
 * The same label rule for statement lines, where the contract already carries
 * a readable label ("Management services"). The contract label wins; the
 * account only supplies the parent when two lines would share a label.
 */
export function disambiguatedLineLabels(
  lines: readonly { account: string; label: string }[],
  companies: AccountLabelContext,
): Map<string, string> {
  return disambiguatedLabels(
    lines.map((line) => {
      // The contract label wins, in the decided words (rd Y0): the pipeline
      // still calls the processor-fee line "Moov fee".
      const provided = lineLabelText(line.label);
      return {
        account: line.account,
        label: provided === "" ? accountShortLabel(line.account, companies) : visible(provided),
      };
    }),
    companies,
  );
}

function disambiguatedLabels(
  rows: readonly { account: string; label: string }[],
  companies: AccountLabelContext,
): Map<string, string> {
  const base = new Map<string, string>();
  const counts = new Map<string, number>();
  for (const row of rows) {
    base.set(row.account, row.label);
    counts.set(row.label, (counts.get(row.label) ?? 0) + 1);
  }
  const out = new Map<string, string>();
  const used = new Set<string>();
  for (const row of rows) {
    const label = base.get(row.account) ?? row.account;
    let shown = label;
    if ((counts.get(label) ?? 0) > 1) {
      const parent = accountParentLabel(row.account, companies);
      shown = parent === "" ? label : `${parent} \u203a ${label}`;
    }
    if (used.has(shown)) {
      const { body } = accountParts(row.account);
      const full = body.map(humaniseAccountSegment).filter((part) => part !== "").join(" \u203a ");
      if (full !== "") shown = full;
    }
    used.add(shown);
    out.set(row.account, visible(shown));
  }
  return out;
}

function sectionDisplayTotal(
  report: ReportId,
  sectionKey: string,
  lines: readonly { amount: string }[],
  fallback: string,
): bigint {
  if (lines.length === 0) {
    return displayCents(fallback, report, sectionKey);
  }
  let total = 0n;
  for (const line of lines) total += displayCents(line.amount, report, sectionKey);
  return total;
}

/** Net income as a person reads it: income minus expenses, in exact cents. */
export function pnlNetCents(data: PnlData): bigint {
  const income = data.sections.find((section) => section.key === "income");
  const expenses = data.sections.find((section) => section.key === "expenses");
  if (income === undefined && expenses === undefined) return decimalCents(data.netIncome);
  const incomeTotal =
    income === undefined ? 0n : sectionDisplayTotal("pnl", "income", income.lines, income.total);
  const expenseTotal =
    expenses === undefined
      ? 0n
      : sectionDisplayTotal("pnl", "expenses", expenses.lines, expenses.total);
  return incomeTotal - expenseTotal;
}

/** An account as words in a text cell: the same label the printed rows use. */
function csvAccount(account: string, companies: AccountLabelContext): string {
  return visible(accountLabel(account, companies, { separator: " · " }));
}

function textButton(
  label: string,
  attrs: Record<string, string>,
  onClick: () => void,
): HTMLButtonElement {
  const button = h("button", { type: "button", class: "report-drill", ...attrs }, [
    visible(label),
  ]);
  button.addEventListener("click", () => {
    onClick();
  });
  return button;
}

/**
 * The comparison column's numbers, keyed the same way the current table's rows
 * are (section + line), so a row finds its counterpart by identity and never
 * by position. A row the comparison period did not have is `0.00`: no
 * postings is a real zero, not a missing figure — the note above the tables
 * says which period was read.
 *
 * The index holds the CONTRACT's sign (the same decimal the payload carries:
 * an expense is negative, a credit balance is negative). The screen flips it
 * per row for the eye, exactly as it flips the Amount column; the CSV keeps it,
 * because the existing single-month export has always carried the contract's
 * own decimals and one row may not mix two conventions.
 */
type CompareIndex = ReadonlyMap<string, bigint>;

interface CompareColumn {
  readonly label: string;
  readonly note: string;
  readonly report: ReportId;
  /** False when the comparison is short and the Difference must not be drawn. */
  readonly difference: boolean;
  readonly index: CompareIndex;
}

function compareKey(sectionKey: string, rowKey: string): string {
  return `${sectionKey}\u0000${rowKey}`;
}

function compareColumn(report: ReportId, compare: ReportCompare): CompareColumn {
  const index = new Map<string, bigint>();
  switch (report) {
    case "pnl": {
      const data = compare.data as PnlData;
      for (const section of data.sections) {
        for (const line of section.lines) {
          index.set(compareKey(section.key, line.account), decimalCents(line.amount));
        }
        index.set(compareKey(section.key, ""), decimalCents(section.total));
      }
      index.set(compareKey("", "net"), decimalCents(data.netIncome));
      break;
    }
    case "balance-sheet": {
      const data = compare.data as BalanceSheetData;
      for (const section of data.sections) {
        for (const line of section.lines) {
          index.set(compareKey(section.key, line.account), decimalCents(line.amount));
        }
        index.set(compareKey(section.key, ""), decimalCents(section.total));
      }
      break;
    }
    case "cash-flow": {
      const data = compare.data as CashFlowData;
      index.set(compareKey("", "opening"), decimalCents(data.openingCash));
      index.set(compareKey("", "closing"), decimalCents(data.closingCash));
      index.set(compareKey("", "net"), decimalCents(data.netChange));
      for (const section of data.sections) {
        for (const line of section.lines) {
          index.set(compareKey(section.key, line.label), decimalCents(line.amount));
        }
        index.set(compareKey(section.key, ""), decimalCents(section.total));
      }
      break;
    }
    case "by-location":
    case "py-comparison":
      // Neither report takes this column: three money columns have no single
      // column to sit under, and the prior-year report already is a
      // comparison. The page does not offer it there.
      break;
    default: {
      const exhaustive: never = report;
      return exhaustive;
    }
  }
  return {
    label: compare.label,
    note: compare.note,
    report,
    difference: compare.difference !== false,
    index,
  };
}

/**
 * One row of the exported table, in the report's own decimals. The Amount,
 * comparison and Difference columns all carry the contract's sign, so the
 * Difference is literally the subtraction of the two columns beside it — for
 * an expense row (-40.00 against -30.00) that is -10.00, and the reader never
 * sees the "-40.00, 30.00, 10.00" mixture that adds up to nothing.
 */
function comparedRow(
  row: readonly string[],
  compare: CompareColumn | null,
  key: string,
  currentContract: bigint,
): string[] {
  if (compare === null) return [...row];
  const other = compare.index.get(key) ?? 0n;
  if (!compare.difference) return [...row, centsToDecimalString(other)];
  return [...row, centsToDecimalString(other), centsToDecimalString(currentContract - other)];
}

function comparedHeaders(headers: readonly string[], compare: CompareColumn | null): string[] {
  if (compare === null) return [...headers];
  return compare.difference
    ? [...headers, compare.label, "Difference"]
    : [...headers, compare.label];
}

function comparedAmountColumns(
  columns: readonly number[],
  width: number,
  compare: CompareColumn | null,
): number[] {
  if (compare === null) return [...columns];
  // The appended columns sit after the original ones, so their indexes are the
  // original width and the one after it.
  return compare.difference ? [...columns, width, width + 1] : [...columns, width];
}

function pnlTable(
  data: PnlData,
  companies: AccountLabelContext,
  compare: CompareColumn | null,
): ShownTable {
  const rows: string[][] = [];
  for (const section of data.sections) {
    for (const line of section.lines) {
      rows.push(
        comparedRow(
          [visible(lineLabelText(line.label)), csvAccount(line.account, companies), line.amount],
          compare,
          compareKey(section.key, line.account),
          decimalCents(line.amount),
        ),
      );
    }
    rows.push(
      comparedRow(
        [`Total ${visible(section.label)}`, "", section.total],
        compare,
        compareKey(section.key, ""),
        decimalCents(section.total),
      ),
    );
  }
  rows.push(
    comparedRow(
      ["Net income", "", data.netIncome],
      compare,
      compareKey("", "net"),
      decimalCents(data.netIncome),
    ),
  );
  if (data.priorYearNetIncome !== null) {
    // The prior-year figure is the payload's own second column, not this
    // lane's comparison column: it stays single, so nothing in the file is
    // ambiguous about which comparison a cell belongs to.
    rows.push(["Prior year net income", "", data.priorYearNetIncome]);
  }
  return {
    headers: comparedHeaders(["Line", "Account", "Amount"], compare),
    rows,
    amountColumns: comparedAmountColumns([2], 3, compare),
  };
}

function balanceTable(
  data: BalanceSheetData,
  companies: AccountLabelContext,
  compare: CompareColumn | null,
): ShownTable {
  const rows: string[][] = [];
  for (const section of data.sections) {
    for (const line of section.lines) {
      rows.push(
        comparedRow(
          [
            balanceSheetLineLabel(line.account, line.label),
            csvAccount(line.account, companies),
            line.amount,
          ],
          compare,
          compareKey(section.key, line.account),
          decimalCents(line.amount),
        ),
      );
    }
    rows.push(
      comparedRow(
        [`Total ${visible(section.label)}`, "", section.total],
        compare,
        compareKey(section.key, ""),
        decimalCents(section.total),
      ),
    );
  }
  return {
    headers: comparedHeaders(["Line", "Account", "Amount"], compare),
    rows,
    amountColumns: comparedAmountColumns([2], 3, compare),
  };
}

function cashTable(data: CashFlowData, compare: CompareColumn | null): ShownTable {
  const rows: string[][] = [
    comparedRow(
      ["Opening cash", data.openingCash],
      compare,
      compareKey("", "opening"),
      decimalCents(data.openingCash),
    ),
  ];
  for (const section of data.sections) {
    for (const line of section.lines) {
      rows.push(
        comparedRow(
          [visible(lineLabelText(line.label)), line.amount],
          compare,
          compareKey(section.key, line.label),
          decimalCents(line.amount),
        ),
      );
    }
    rows.push(
      comparedRow(
        [`Total ${visible(section.label)}`, section.total],
        compare,
        compareKey(section.key, ""),
        decimalCents(section.total),
      ),
    );
  }
  rows.push(
    comparedRow(
      ["Net change", data.netChange],
      compare,
      compareKey("", "net"),
      decimalCents(data.netChange),
    ),
    comparedRow(
      ["Closing cash", data.closingCash],
      compare,
      compareKey("", "closing"),
      decimalCents(data.closingCash),
    ),
  );
  return {
    headers: comparedHeaders(["Line", "Amount"], compare),
    rows,
    amountColumns: comparedAmountColumns([1], 2, compare),
  };
}

/**
 * By Location has three money columns per row, so this lane's one comparison
 * column has no single column to sit under: a comparison of "Net" would read
 * as the comparison of Income and Expenses too. It keeps its own three
 * columns and the page hides the comparison control on this report.
 */
function locationTable(data: ByLocationData): ShownTable {
  const rows = [...data.locations, ...(data.parents ?? [])].map((row) => [
    visible(row.location),
    row.income,
    row.expenses,
    row.net,
  ]);
  return {
    headers: ["Location", "Income", "Expenses", "Net"],
    rows,
    amountColumns: [1, 2, 3],
  };
}

function pyTable(data: PyComparisonData): ShownTable | null {
  if (!data.available || data.lines.length === 0) return null;
  return {
    headers: ["Line", "Current", "Prior"],
    rows: data.lines.map((line) => [visible(lineLabelText(line.label)), line.current, line.prior]),
    amountColumns: [1, 2],
  };
}

function renderPnlTable(
  container: HTMLElement,
  snapshot: ReportSnapshot<PnlData>,
  options: StatementRenderOptions,
): Chart[] {
  clear(container);
  const data = snapshot.data;
  const charts: Chart[] = [];
  const compare = options.compare === undefined ? undefined : compareColumn("pnl", options.compare);
  const labels = disambiguatedLineLabels(
    data.sections.flatMap((section) => section.lines),
    options.companies,
  );
  container.append(
    h("h2", { class: "gvf-section-title" }, ["Profit & Loss"]),
    h("p", { class: "gvf-meta" }, [
      coverageMeta(data.periodStart, data.periodEnd, data.currency),
    ]),
  );
  const compareLine = compareNote(compare);
  if (compareLine !== null) container.append(compareLine);
  pushPnlChart(container, charts, snapshot);
  for (const section of data.sections) {
    container.append(pnlSectionTable(section, options, labels, compare));
  }
  const net = pnlNetCents(data);
  container.append(headline(net < 0n ? "Net loss" : "Net income", data.netIncome, net, "net-income"));
  if (compare !== undefined) {
    const other = compare.index.get(compareKey("", "net")) ?? 0n;
    if (compare.difference) {
      container.append(
        h("p", { class: "gvf-meta", "data-role": "net-compare" }, [
          `${net < 0n ? "Net loss" : "Net income"} in ${visible(compare.label)}: `,
          amountSpan(centsToDecimalString(other)),
          " · Difference: ",
          amountSpan(centsToDecimalString(net - other)),
        ]),
      );
    } else {
      // A short comparison has no period figure to subtract from, so the
      // sentence gives the figure it does have and no difference.
      container.append(
        h("p", { class: "gvf-meta", "data-role": "net-compare" }, [
          `${net < 0n ? "Net loss" : "Net income"} in ${visible(compare.label)}: `,
          amountSpan(centsToDecimalString(other)),
          ", from the months loaded so far.",
        ]),
      );
    }
  }
  if (data.priorYearNetIncome !== null) {
    container.append(
      h("p", { class: "gvf-meta" }, [
        "Prior-year net income: ",
        amountSpan(data.priorYearNetIncome),
      ]),
    );
  }
  return charts;
}

function pnlSectionTable(
  section: PnlSection,
  options: StatementRenderOptions,
  labels: ReadonlyMap<string, string>,
  compare: CompareColumn | undefined,
): HTMLElement {
  const prefix = groupAccountPrefix(section.lines.map((line) => line.account));
  const rows = section.lines.map((line) =>
    pnlLineRow(line, options, labels, section.key, compare),
  );
  const totalLabel = `Total ${section.label}`;
  const totalCell =
    prefix !== null && options.onPrefix !== undefined
      ? textButton(totalLabel, { "data-action": "drill-prefix", "data-account-prefix": prefix }, () => {
          options.onPrefix?.(prefix);
        })
      : visible(totalLabel);
  const total = sectionDisplayTotal("pnl", section.key, section.lines, section.total);
  rows.push(
    h("tr", { class: "gvf-total" }, [
      h("td", {}, [totalCell]),
      amountCell(section.total, total),
      ...compareCells(compare, compareKey(section.key, ""), section.key, total),
    ]),
  );
  return h("table", { class: "gvf-table" }, [
    h("caption", {}, [visible(section.label)]),
    h("thead", {}, [
      h("tr", {}, [
        h("th", {}, ["Line"]),
        h("th", { class: "gvf-num" }, ["Amount"]),
        ...compareHeadCells(compare),
      ]),
    ]),
    h("tbody", {}, rows),
  ]);
}

function pnlLineRow(
  line: PnlLine,
  options: StatementRenderOptions,
  labels: ReadonlyMap<string, string>,
  sectionKey: string,
  compare: CompareColumn | undefined,
): HTMLTableRowElement {
  const labelText = labels.get(line.account) ?? visible(lineLabelText(line.label));
  const label =
    line.account !== "" && options.onAccount !== undefined
      ? textButton(labelText, { "data-action": "drill-account", "data-account": line.account }, () => {
          options.onAccount?.(line.account);
        })
      : visible(labelText);
  const displayed = displayCents(line.amount, "pnl", sectionKey);
  return h("tr", {}, [
    h("td", {}, [label]),
    amountCell(line.amount, displayed),
    ...compareCells(compare, compareKey(sectionKey, line.account), sectionKey, displayed),
  ]);
}

/**
 * The equity line that carries every profit not yet moved into equity. The
 * pipeline adds cumulative P&L through the as-of date to the equity section
 * (`pipeline/reports.py`, `build_balance_sheet_data`: `current_earnings`), and
 * older/demo statements call that line "Unclosed income and expense". The name
 * says nothing to a reader, so it is said in words here.
 */
const UNCLOSED_ACCOUNT_LEAF = "unclosed";
const UNCLOSED_LEGACY_LABEL = "unclosed income and expense";
const UNCLOSED_LABEL = "Profit so far, not closed yet";
/**
 * What the line is. Provable from the pipeline's own definition: it is the
 * cumulative profit since the books started (income and expenses through the
 * as-of date), while a Profit & Loss report covers one period. No figure is
 * repeated, so the sentence cannot go stale.
 */
const UNCLOSED_EXPLANATION =
  "Every profit since the books started that has not been moved into equity yet. " +
  "The Profit & Loss report covers one period, so its net can differ from this line.";

/**
 * One balance-sheet line's words: the earnings line gets the plain name above,
 * everything else keeps the contract label through `lineLabelText`. Exported so
 * the printed statement and the CSV use the same words as the screen.
 */
export function balanceSheetLineLabel(account: string, label: string): string {
  return isUnclosedEarningsLine(account, label)
    ? UNCLOSED_LABEL
    : visible(lineLabelText(label));
}

function isUnclosedEarningsLine(account: string, label: string): boolean {
  // A comparison against the "Unclosed" marker: the words are never shown, so
  // this asks only for the leaf and needs no company names.
  if (accountLeafLabel(account, NO_COMPANY_NAMES).toLowerCase() === UNCLOSED_ACCOUNT_LEAF) return true;
  return label.trim().toLowerCase() === UNCLOSED_LEGACY_LABEL;
}

function renderBalanceTable(
  container: HTMLElement,
  snapshot: ReportSnapshot<BalanceSheetData>,
  options: StatementRenderOptions,
): Chart[] {
  clear(container);
  const data = snapshot.data;
  // rd13 L1: a balance sheet always says which day it is for. One date only:
  // the page's own as-of wins, because in an open month the payload's month
  // end and the day the books actually reach are different days.
  const asOf = (options.asOfText ?? "").trim() || formatCivil(data.asOf);
  const meta =
    asOf === "" ? data.currency : `As of ${asOf} · ${data.currency}`;
  container.append(
    h("h2", { class: "gvf-section-title" }, ["Balance Sheet"]),
    h("p", { class: "gvf-meta", "data-role": "balance-sheet-meta" }, [meta]),
  );
  const compare =
    options.compare === undefined ? undefined : compareColumn("balance-sheet", options.compare);
  const compareLine = compareNote(compare);
  if (compareLine !== null) container.append(compareLine);
  const labels = disambiguatedLineLabels(
    data.sections.flatMap((section) => section.lines),
    options.companies,
  );
  for (const section of data.sections) {
    const rows = section.lines.map((line) => {
      const closing = options.closings?.get(line.account);
      const drillable =
        closing !== undefined &&
        sameMoney(line.amount, closing) &&
        options.onAccount !== undefined;
      const labelText = isUnclosedEarningsLine(line.account, line.label)
        ? UNCLOSED_LABEL
        : (labels.get(line.account) ?? balanceSheetLineLabel(line.account, line.label));
      const label = drillable
        ? textButton(
            labelText,
            { "data-action": "drill-balance", "data-account": line.account },
            () => {
              options.onAccount?.(line.account);
            },
          )
        : visible(labelText);
      const displayed = displayCents(line.amount, "balance-sheet", section.key);
      return h("tr", {}, [
        h("td", {}, [label]),
        amountCell(line.amount, displayed),
        ...compareCells(compare, compareKey(section.key, line.account), section.key, displayed),
      ]);
    });
    const total = sectionDisplayTotal("balance-sheet", section.key, section.lines, section.total);
    rows.push(
      h("tr", { class: "gvf-total" }, [
        h("td", {}, [`Total ${visible(section.label)}`]),
        amountCell(section.total, total),
        ...compareCells(compare, compareKey(section.key, ""), section.key, total),
      ]),
    );
    container.append(
      h("table", { class: "gvf-table" }, [
        h("caption", {}, [visible(section.label)]),
        h("thead", {}, [
          h("tr", {}, [
            h("th", {}, ["Account"]),
            h("th", { class: "gvf-num" }, ["Balance"]),
            ...compareHeadCells(compare),
          ]),
        ]),
        h("tbody", {}, rows),
      ]),
    );
    if (section.lines.some((line) => isUnclosedEarningsLine(line.account, line.label))) {
      // One sentence, right under the table it explains.
      container.append(
        h(
          "p",
          { class: "gvf-meta", "data-role": "unclosed-explanation" },
          [UNCLOSED_EXPLANATION],
        ),
      );
    }
  }
  return [];
}

function renderCashTable(
  container: HTMLElement,
  snapshot: ReportSnapshot<CashFlowData>,
  options: StatementRenderOptions,
): Chart[] {
  clear(container);
  const data = snapshot.data;
  const charts: Chart[] = [];
  const compare =
    options.compare === undefined ? undefined : compareColumn("cash-flow", options.compare);
  container.append(
    h("h2", { class: "gvf-section-title" }, ["Cash Flow"]),
    h("p", { class: "gvf-meta" }, [
      coverageMeta(data.periodStart, data.periodEnd, data.currency),
    ]),
  );
  const compareLine = compareNote(compare);
  if (compareLine !== null) container.append(compareLine);
  if (data.sections.length > 0) {
    try {
      const chart = barChartBlock(
        container,
        data.sections.map((section) => visible(section.label)),
        [
          {
            label: "Section total",
            values: data.sections.map((section) => toChartNumber(parseDecimal(section.total))),
            color: SERIES_COLORS.a,
          },
        ],
        {
          label: "Net change",
          values: data.sections.map(() => toChartNumber(parseDecimal(data.netChange))),
        },
        `Cash flow by section for ${snapshot.period}, with net change`,
      );
      if (chart) charts.push(chart);
    } catch {
      /* chart is optional; the table is the statement */
    }
  }
  for (const section of data.sections) {
    const rows = section.lines.map((line) => {
      const displayed = displayCents(line.amount, "cash-flow", section.key);
      const accounts = line.accounts ?? [];
      // A line is a control only when the pipeline named the accounts it
      // bucketed (rd15 A11). An older saved month has none, and a button that
      // opened an unfiltered journal would show every account.
      const label =
        accounts.length > 0 && options.onAccounts !== undefined
          ? textButton(
              lineLabelText(line.label),
              {
                "data-action": "drill-line",
                "data-accounts": accounts.join(","),
              },
              () => {
                options.onAccounts?.(accounts);
              },
            )
          : visible(lineLabelText(line.label));
      return h("tr", {}, [
        h("td", {}, [label]),
        amountCell(line.amount, displayed),
        ...compareCells(compare, compareKey(section.key, line.label), section.key, displayed),
      ]);
    });
    const total = sectionDisplayTotal("cash-flow", section.key, section.lines, section.total);
    rows.push(
      h("tr", { class: "gvf-total" }, [
        h("td", {}, [`Total ${visible(section.label)}`]),
        amountCell(section.total, total),
        ...compareCells(compare, compareKey(section.key, ""), section.key, total),
      ]),
    );
    container.append(
      h("table", { class: "gvf-table" }, [
        h("caption", {}, [visible(section.label)]),
        h("thead", {}, [
          h("tr", {}, [
            h("th", {}, ["Line"]),
            h("th", { class: "gvf-num" }, ["Amount"]),
            ...compareHeadCells(compare),
          ]),
        ]),
        h("tbody", {}, rows),
      ]),
    );
  }
  container.append(
    headline("Opening cash", data.openingCash),
    headline("Closing cash", data.closingCash),
    h("p", { class: "gvf-meta" }, ["Net change: ", amountSpan(data.netChange)]),
  );
  if (compare !== undefined) {
    const other = compare.index.get(compareKey("", "net")) ?? 0n;
    container.append(
      h("p", { class: "gvf-meta", "data-role": "net-compare" }, compare.difference
        ? [
            `Net change in ${visible(compare.label)}: `,
            amountSpan(centsToDecimalString(other)),
            " · Difference: ",
            amountSpan(centsToDecimalString(decimalCents(data.netChange) - other)),
          ]
        : [
            `Net change in ${visible(compare.label)}: `,
            amountSpan(centsToDecimalString(other)),
            ", from the months loaded so far.",
          ]),
    );
  }
  return charts;
}

function renderLocationTable(
  container: HTMLElement,
  snapshot: ReportSnapshot<ByLocationData>,
  options: StatementRenderOptions,
): Chart[] {
  clear(container);
  const data = snapshot.data;
  const charts: Chart[] = [];
  const leaves = data.locations;
  container.append(
    h("h2", { class: "gvf-section-title" }, ["By Location"]),
    // A month in words; the currency token is dropped (rd-polish-rules §1 bans
    // "42.00 USD" on screen and every amount here already reads as money).
    h("p", { class: "gvf-meta" }, [monthText(snapshot.period) || visible(snapshot.period)]),
  );
  if (leaves.length > 0) {
    try {
      const chart = barChartBlock(
        container,
        leaves.map((row) => row.location),
        [
          {
            label: "Income",
            values: leaves.map((row) => toChartNumber(parseDecimal(row.income))),
            color: SERIES_COLORS.a,
          },
          {
            label: "Expenses",
            values: leaves.map((row) => toChartNumber(absCents(parseDecimal(row.expenses)))),
            color: SERIES_COLORS.b,
          },
        ],
        null,
        `Income versus expenses by location for ${monthText(snapshot.period) || snapshot.period}`,
      );
      if (chart) charts.push(chart);
    } catch {
      /* table still stands */
    }
  }
  // rd15 A12 restores the leaf drill: the ledger's location filter now folds a
  // stored retired spelling to the current name (the same fold
  // `pipeline/reports.py#_resolve_report_location` uses, published with the
  // month), so a row opens a journal that holds the row's own entries. A parent
  // is several locations and stays plain: no stored string names it.
  container.append(locationRows("Location detail", leaves, options.onLocation));
  const parents = data.parents ?? [];
  if (parents.length > 0) {
    container.append(
      h("h3", { class: "gvf-section-title" }, ["Parent roll-up"]),
      locationRows("Parent roll-up detail", parents, undefined),
    );
  }
  return charts;
}

function locationRows(
  caption: string,
  rows: readonly { location: string; income: string; expenses: string; net: string }[],
  onLocation: ((location: string) => void) | undefined,
): HTMLElement {
  return h("table", { class: "gvf-table" }, [
    h("caption", {}, [caption]),
    h("thead", {}, [
      h("tr", {}, [
        h("th", {}, ["Location"]),
        h("th", { class: "gvf-num" }, ["Income"]),
        h("th", { class: "gvf-num" }, ["Expenses"]),
        h("th", { class: "gvf-num" }, ["Net"]),
      ]),
    ]),
    h("tbody", {}, rows.map((row) => h("tr", {}, [
      h(
        "td",
        {},
        [
          onLocation === undefined
            ? visible(row.location)
            : textButton(
                visible(row.location),
                { "data-action": "drill-location", "data-location": row.location },
                () => {
                  onLocation(row.location);
                },
              ),
        ],
      ),
      amountCell(row.income),
      // The same flip the P&L uses, applied once, to the display only: the
      // number behind it (`data-amount`) stays the contract's own decimal, so
      // no total can move.
      amountCell(row.expenses, displayCents(row.expenses, "pnl", "expenses")),
      amountCell(row.net),
    ]))),
  ]);
}

function renderPyTable(
  container: HTMLElement,
  snapshot: ReportSnapshot<PyComparisonData>,
): Chart[] {
  clear(container);
  const data = snapshot.data;
  container.append(
    h("h2", { class: "gvf-section-title" }, ["Prior-Year Comparison"]),
    h("p", { class: "gvf-meta" }, [
      `${snapshot.period} vs ${data.priorPeriod} · ${data.currency}`,
    ]),
  );
  if (!data.available) {
    const note =
      data.note !== undefined && data.note !== ""
        ? visible(data.note)
        : "Prior-year comparison activates after 13 months of data.";
    container.append(
      h("div", { class: "gvf-state", "data-testid": "py-unavailable" }, [
        h("span", { class: "gvf-state-title" }, ["Not available yet"]),
        note,
      ]),
    );
    return [];
  }
  if (data.lines.length === 0) {
    container.append(h("div", { class: "gvf-state" }, [NO_COMPARISON_LINES_TEXT]));
    return [];
  }
  container.append(
    h("table", { class: "gvf-table" }, [
      h("caption", {}, ["Comparison"]),
      h("thead", {}, [
        h("tr", {}, [
          h("th", {}, ["Line"]),
          h("th", { class: "gvf-num" }, ["Current"]),
          h("th", { class: "gvf-num" }, ["Prior"]),
        ]),
      ]),
      h("tbody", {}, data.lines.map((line) =>
        h("tr", {}, [
          h("td", {}, [visible(lineLabelText(line.label))]),
          amountCell(line.current),
          amountCell(line.prior),
        ]),
      )),
    ]),
  );
  return [];
}

function headline(
  label: string,
  natural: string,
  displayed?: bigint,
  testId?: string,
): HTMLElement {
  const value = amountSpan(natural, displayed);
  value.classList.add("gvf-headline-value");
  if (testId !== undefined) value.setAttribute("data-testid", testId);
  return h("div", { class: "gvf-headline" }, [
    h("span", { class: "gvf-headline-label" }, [label]),
    h("span", { class: "gvf-headline-rule" }),
    value,
  ]);
}

function coverageMeta(periodStart: string, periodEnd: string, currency: string): string {
  if (periodStart === "" || periodEnd === "") return currency;
  // Same style as the page's coverage line beside the report: "Oct 1 to
  // Oct 25, 2026", not "Oct 1, 2026 to Oct 25, 2026".
  const sameYear = periodStart.slice(0, 4) === periodEnd.slice(0, 4);
  const start = sameYear
    ? formatShortDate(periodStart).replace(/,\s*\d{4}$/, "")
    : formatShortDate(periodStart);
  return `${start} to ${formatShortDate(periodEnd)} · ${currency}`;
}

const SHORT_MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
] as const;

/** "Oct 25, 2026" — the console's short civil date. */
export function formatShortDate(iso: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (match === null) return iso;
  const year = match[1];
  const month = SHORT_MONTHS[Number(match[2]) - 1];
  const day = Number(match[3]);
  if (year === undefined || month === undefined || day < 1) return "";
  return `${month} ${String(day)}, ${year}`;
}

function pushPnlChart(
  container: HTMLElement,
  charts: Chart[],
  snapshot: ReportSnapshot<PnlData>,
): void {
  const income = snapshot.data.sections.find((section) => section.key === "income");
  const expenses = snapshot.data.sections.find((section) => section.key === "expenses");
  if (income === undefined || expenses === undefined) return;
  try {
    const incomeCents = sectionDisplayTotal("pnl", "income", income.lines, income.total);
    const expenseCents = sectionDisplayTotal("pnl", "expenses", expenses.lines, expenses.total);
    const chart = barChartBlock(
      container,
      [snapshot.period],
      [
        {
          label: "Income",
          values: [toChartNumber(incomeCents)],
          color: SERIES_COLORS.a,
        },
        {
          label: "Expenses",
          values: [toChartNumber(expenseCents)],
          color: SERIES_COLORS.b,
        },
      ],
      { label: "Net income", values: [toChartNumber(pnlNetCents(snapshot.data))] },
      // The chart's accessible description is words a person hears, so the
      // period reads "October 2026", never the wire's "2026-10" (the same rule
      // the by-location chart beside it already follows).
      `Income versus expenses for ${monthText(snapshot.period) || snapshot.period}, with net income`,
    );
    if (chart) charts.push(chart);
  } catch {
    /* chart is optional */
  }
}

function formatCivil(iso: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  // "" when there is no readable date: a raw machine date is never printed.
  if (match === null) return "";
  const year = match[1];
  const month = MONTH_NAMES[Number(match[2]) - 1];
  const day = Number(match[3]);
  if (year === undefined || month === undefined || day < 1) return "";
  return `${month} ${String(day)}, ${year}`;
}
